import React from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Table, Space, DatePicker, Button, Input } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import type { TablePaginationConfig } from 'antd'
import { Address } from 'types/address'
import { FreeAnswer } from 'types/freeAnswer'
import { Pagination } from 'types/pagination'

interface Props {
  freeAnswers: FreeAnswer[]
  addresses: Address[]
  pagination: Pagination
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, any>
  ) => void
  filters: Filter[]
}

interface Filter {
  field: string
  values: string[]
}

const Tables: React.FC<Props> = ({
  freeAnswers,
  addresses,
  pagination,
  onTableChange,
  filters,
}) => {
  const { t } = useTranslation()
  const { RangePicker } = DatePicker

  const dataSource = freeAnswers.map((freeAnswer: FreeAnswer) => ({
    key: freeAnswer.id,
    forwardedAt: dayjs(freeAnswer.forwardedAt).format('DD/MM/YYYY'),
    createdAt: dayjs(freeAnswer.createdAt).format('DD/MM/YYYY'),
    trackingNumber: freeAnswer.trackingNumber,
    center: freeAnswer.addressName,
  }))

  const forwardedAtValues: string[] | undefined = filters.find(
    el => el.field === 'forwarded_at'
  )?.values
  const createdAtValues: string[] | undefined = filters.find(
    el => el.field === 'created_at'
  )?.values

  const columns = [
    {
      title: t('common:forwarded-at'),
      dataIndex: 'forwardedAt',
      key: 'forwardedAt',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Space>
            <RangePicker
              format={'DD/MM/YYYY'}
              onChange={e => setSelectedKeys([e])}
              allowClear={true}
              defaultValue={
                forwardedAtValues
                  ? [dayjs(forwardedAtValues[0]), dayjs(forwardedAtValues[1])]
                  : undefined
              }
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters()
                confirm()
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filters.find(el => el.field === 'forwarded_at')?.values,
    },
    {
      title: t('common:created-at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Space>
            <RangePicker
              format={'DD/MM/YYYY'}
              onChange={e => setSelectedKeys([e])}
              allowClear={true}
              defaultValue={
                createdAtValues
                  ? [dayjs(createdAtValues[0]), dayjs(createdAtValues[1])]
                  : undefined
              }
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters()
                confirm()
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filters.find(el => el.field === 'created_at')?.values,
    },
    {
      title: t('common:tracking-number'),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Space>
            <Input
              onChange={e => setSelectedKeys([e.target.value])}
              allowClear={true}
              defaultValue={
                filters.find(el => el.field === 'tracking_number')?.values[0]
              }
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters()
                confirm()
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filters.find(el => el.field === 'tracking_number')?.values,
    },
    {
      title: t('common:center'),
      dataIndex: 'center',
      key: 'center',
      filters: addresses.map((address: Address) => ({
        text: address.shortname,
        value: `${address.id}`,
      })),
      filteredValue: filters.find(el => el.field === 'address_ids')?.values,
    },
  ]

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageLength,
          total: pagination.total,
          showSizeChanger: true,
        }}
        onChange={onTableChange}
      />
    </Space>
  )
}

export default Tables
